import React from "react";
import { usePageView } from "../../utils/track";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BarChart3, Calendar, Landmark, Layers, MapPin, Share2, Users } from "lucide-react";
import { Breadcrumb } from "../../layouts/PublicLayout";
import { Badge, EmptyState, InfoRow, Loading, SectionCard, statusTone } from "../../components/ui";
import CompetitionSidebar from "../../components/CompetitionSidebar";
import { useApi } from "../../api/client";
import { Aggregates, Competition, CompetitionEvent } from "../../data/types";
import { usePortalLink } from "../../utils/portalLink";
import { rowClick } from "../../utils/rowClick";
import { formatScoreCentres, formatAverage } from "../../utils/score";

export default function CompetitionDetail() {
  usePageView("event");
  const link = usePortalLink();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: c, loading } = useApi<(Competition & { eventList?: CompetitionEvent[] }) | null>(
    id ? `/competitions/${id}` : null,
    null
  );
  const { data: agg } = useApi<Aggregates | null>(id ? `/competitions/${id}/aggregates` : null, null);

  if (loading) return <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5"><div className="card"><Loading /></div></div>;
  if (!c) {
    return (
      <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
        <div className="card">
          <EmptyState
            title="Event not found"
            message="It may have been removed, or no shoot has been imported yet."
            action={
              <Link to={link("/competitions")} className="text-[13px] font-bold text-primary hover:underline">
                Back to events
              </Link>
            }
          />
        </div>
      </div>
    );
  }

  const schedule = c.eventList ?? [];
  /*
   * The aggregate arrives grouped by class. These summary figures are about the
   * competition as a whole, so they read across every class — but "best" is
   * deliberately the leader of each class rather than one overall winner, since
   * the classes are not comparable.
   */
  const grand = (agg?.grand ?? []).flatMap((g) => g.rows);
  const classLeaders = (agg?.grand ?? []).map((g) => ({ rifleClass: g.rifleClass, leader: g.rows[0] })).filter((x) => x.leader);
  const avgScore = grand.length ? grand.reduce((a, r) => a + r.score, 0) / grand.length : 0;

  const stats = [
    /*
     * The competitions shot within this event — its CIDs — not the days it
     * spans. A two-day meeting for the Bronger and the Carrington is ONE event
     * holding TWO competitions, and the day count already sits beside the date
     * range above.
     */
    /*
     * Competitions and Matches are counted beside their own rows in the
     * navigation to the left, so they are not tiled here as well. What is left
     * is what the sidebar cannot say: who turned up, from where, and how much
     * shooting is actually on record.
     */
    { icon: Users, value: c.shooters, label: "Shooters" },
    /* A club is who a competitor belongs to; a team is what they were picked
       for at this meeting. Most competitors have the first and not the second,
       so one tile counting both told you neither. */
    { icon: Landmark, value: c.clubs, label: "Clubs" },
    { icon: Layers, value: c.teams ?? 0, label: "Teams" },
    { icon: BarChart3, value: (c.stringsRecorded ?? c.stringsPlanned ?? 0).toLocaleString(), label: "Strings Recorded" },
  ];

  return (
    <div className="mx-auto max-w-[1720px] px-4 py-4 sm:px-5">
      <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Events", to: "/competitions" }, { label: c.name }]} />

      <div className="mt-3 flex flex-col gap-4 lg:flex-row">
        <CompetitionSidebar competition={c} active="overview" events={c.eventList} />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight text-ink">{c.name}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted">
                <span className="flex items-center gap-1.5"><Calendar size={15} /> {c.startDate} - {c.endDate} ({c.days} Days)</span>
                <span className="flex items-center gap-1.5"><MapPin size={15} className="text-accent" /> {c.range}, {c.stateCode}, {c.country}</span>
              </div>
              {c.status === "LIVE" && (
                <div className="mt-3 flex items-center gap-2.5">
                  <Badge tone="live">LIVE</Badge>
                  <span className="text-sm font-semibold text-ink">{c.statusDetail} in progress</span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2.5">
              {/*
                The one thing almost everybody opens this page to reach. It was
                the same size as Share beside it, which said they were equally
                likely — they are not, by a long way.
              */}
              <Link
                to={link(`/competitions/${c.id}/results`)}
                className="inline-flex h-14 items-center gap-2.5 rounded-xl bg-primary px-7 text-base font-extrabold text-white shadow-md transition-colors hover:bg-primary-hover"
              >
                <BarChart3 size={20} /> View Results
              </Link>
              <button className="inline-flex h-10 items-center gap-2 rounded-lg border border-line bg-white px-4 text-sm font-semibold text-ink transition-colors hover:bg-slate-50">
                <Share2 size={15} /> Share
              </button>
            </div>
          </div>

          {/* Stats strip */}
          <div className="card mt-4 grid grid-cols-2 divide-x divide-line sm:grid-cols-3 lg:grid-cols-5">
            {stats.map((s) => (
              <div key={s.label} className="flex items-center gap-3.5 px-5 py-5">
                <s.icon size={26} className="shrink-0 text-primary" />
                <div>
                  <div className="text-2xl font-bold leading-none text-ink">{s.value}</div>
                  <div className="mt-1.5 text-[13px] font-medium text-muted">{s.label}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.35fr]">
            <div className="space-y-6">
              <SectionCard title="About This Event" bodyClassName="p-4">
                <p className="text-sm leading-relaxed text-slate-600">{c.description}</p>
                <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-[13px] sm:grid-cols-2">
                  {[
                    { label: "Organiser", value: c.organiser },
                    /* "Sanctioned by" ran three bodies together in one line. They
                       are not equivalent: the state body runs the shoot, the
                       national and international ones recognise it. */
                    { label: "State Peak Body", value: c.associationState },
                    { label: "Other Bodies", value: [c.associationNational, c.associationIntl].filter(Boolean).join(" · ") },
                    { label: "Match Director", value: c.matchDirector },
                    { label: "Contact Email", value: c.contactEmail },
                    ...(c.chiefRangeOfficer ? [{ label: "Chief Range Officer", value: c.chiefRangeOfficer }] : []),
                    { label: "Last Updated",
                      value: c.updatedAt ? new Date(c.updatedAt).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "—",
                    },
                  ].map((r) => (
                    <div key={r.label} className="flex items-baseline justify-between gap-3 border-b border-line py-1 last:border-0">
                      <dt className="shrink-0 text-muted">{r.label}</dt>
                      <dd className="truncate text-right font-semibold text-ink">{r.value || "—"}</dd>
                    </div>
                  ))}
                </dl>
              </SectionCard>

              {/* Computed from the imported results — no figure here is hard-coded. */}
              <SectionCard title="Statistics" bodyClassName="px-5 py-3">
                <InfoRow label="Strings Recorded" value={(c.stringsRecorded ?? 0).toLocaleString()} />
                <InfoRow label="Ranges" value={c.matches} />
                <InfoRow label="Entries Scored" value={grand.length} />
                <InfoRow label="Average Aggregate" value={grand.length ? formatAverage(avgScore) : "—"} />
                {/* One leader per class — a single "high aggregate" across
                    classes would just name whichever class scores highest. */}
                <InfoRow
                  label={classLeaders.length > 1 ? "High Aggregate by Class" : "High Aggregate"}
                  value={
                    classLeaders.length ? (
                      <span className="flex flex-col items-end gap-0.5">
                        {classLeaders.map(({ rifleClass, leader }) => (
                          <span key={rifleClass}>
                            <span className="text-muted">{rifleClass}</span>{" "}
                            {formatScoreCentres(leader.score, leader.xCount, leader.centreLabel)}{" "}
                            <span className="text-muted">({leader.shooter})</span>
                          </span>
                        ))}
                      </span>
                    ) : (
                      "—"
                    )
                  }
                />
              </SectionCard>
            </div>

            <SectionCard
              title="Schedule"
              action={<span className="cursor-pointer text-sm font-semibold text-primary hover:underline">View Full Schedule</span>}
            >
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="th">Date</th>
                    <th className="th">Day</th>
                    <th className="th">Event / Match</th>
                    <th className="th text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {!schedule.length && (
                    <tr>
                      <td colSpan={4} className="td py-8 text-center text-muted">
                        No events scheduled for this competition.
                      </td>
                    </tr>
                  )}
                  {schedule.map((e) => (
                    <tr
                      key={e.id}
                      onClick={rowClick(() => navigate(link(`/competitions/${c.id}/events/${e.id}`)))}
                      className={`cursor-pointer transition-colors hover:bg-slate-50 ${e.status === "LIVE" ? "bg-success-soft/60" : ""}`}
                    >
                      <td className="td">{e.date}</td>
                      <td className="td text-muted">{e.day}</td>
                      <td className="td font-semibold">
                        <Link to={link(`/competitions/${c.id}/events/${e.id}`)} className="hover:text-primary">
                          Event {e.n}: {e.name}
                        </Link>
                      </td>
                      <td className="td text-right">
                        <Badge tone={statusTone(e.status)}>{e.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="p-4">
                <Link
                  to={link(`/competitions/${c.id}/matches`)}
                  className="flex h-10 w-full items-center justify-center rounded-lg border border-line text-sm font-bold text-primary transition-colors hover:bg-primary-soft"
                >
                  View All Matches
                </Link>
              </div>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}
